const MAX_QUERY_LENGTH = 2000; // Maximum allowed query length
const MAX_HEADERS_SIZE = 20 * 1024; // Maximum header size in bytes (20 KB)

/**
 * Calculate the size of the headers in bytes
 */
function calculateHeadersSize(headers: HeadersInit): number {
	let totalBytes = 0;
	const textEncoder = new TextEncoder();

	// Iterate over each header entry (name and value)
	if (headers instanceof Headers) {
		headers.forEach((value: string, name: string) => {
			const headerSize = textEncoder.encode(`${name}: ${value}\r\n`).length; // Include CRLF
			totalBytes += headerSize;
		});
	} else if (Array.isArray(headers)) {
		for (const [name, value] of headers) {
			const headerSize = textEncoder.encode(`${name}: ${value}\r\n`).length;
			totalBytes += headerSize;
		}
	} else {
		for (const [name, value] of Object.entries(headers)) {
			const headerSize = textEncoder.encode(`${name}: ${value}\r\n`).length;
			totalBytes += headerSize;
		}
	}

	return totalBytes;
}

type GraphQLPayload = Array<{
	operationName: string;
	query: string;
	variables: { [key: string]: any };
}>;

/**
 * Determines if a GraphQL request should use GET method instead of POST.
 * Returns true if:
 * - Not running in SSR
 * - All operations are queries (not mutations or subscriptions)
 * - URL + variables length <= MAX_QUERY_LENGTH
 * - Headers size <= MAX_HEADERS_SIZE
 */
export function shouldUseGetMethod({
	url,
	variables,
	headers,
	payload,
}: {
	url: string;
	variables: { [key: string]: any };
	headers?: HeadersInit;
	payload: GraphQLPayload;
}): { isGetRequest: boolean; reason?: string } {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR === 'true') {
		return { isGetRequest: false, reason: 'SSR' };
	}

	// Check if all operations are queries (not mutations or subscriptions)
	const hasNonQueryOperation = payload.some((item) => {
		const query = item.query || '';
		// Check if it's a mutation or subscription
		return query.trim().startsWith('mutation') || query.trim().startsWith('subscription');
	});

	if (hasNonQueryOperation) {
		return { isGetRequest: false, reason: 'non-query operation' };
	}

	const serializedVariables = JSON.stringify(variables);
	const queryLength = url.length + serializedVariables.length;
	const headersSize = calculateHeadersSize(headers || {});
	return queryLength <= MAX_QUERY_LENGTH && headersSize <= MAX_HEADERS_SIZE
		? { isGetRequest: true }
		: { isGetRequest: false, reason: 'URL length or headers size too large' };
}
